import React, { useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import { useLicenseInfo } from '../../components/services/guideService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { FiCopy, FiCheck, FiDownload } from 'react-icons/fi';
import { FaSpinner } from 'react-icons/fa';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import api from '../../components/services/apiService';
import moment from 'moment';

const GuidePage = () => {
  const { getLicenseInfo } = useLicenseInfo();
  const [licenseData, setLicenseData] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'success'>('idle');

  const defaultLicenseData = {
    licenseKey: 'XXXX-XXXX-XXXX-XXXX',
    activateToDate: null,
    totalLicenses: 0,
  };

  const handleFetchLatestAgent = async () => {
    setDownloadState('downloading');
    try {
      const response = await api.get('/agentapi/AgentRegistration/download-latest-agent', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);

      const disposition = response.headers['content-disposition'];
      let filename = 'PatchifiAgent.msi';
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setDownloadState('success');
      addToast('Agent downloaded successfully!', 'success', 4000);

      setTimeout(() => setDownloadState('idle'), 3000);
    } catch (error) {
      setDownloadState('idle');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!selectedTenantId) {
          setLicenseData(defaultLicenseData);
          return;
        }

        const result = await getLicenseInfo(selectedTenantId);
        if (result?.data) {
          setLicenseData(result.data);
        } else {
          setLicenseData(defaultLicenseData);
        }
      } catch (err) {
        console.error('Error fetching license info:', err);
        setLicenseData(defaultLicenseData);
      }
    };

    fetchData();
  }, [selectedTenantId]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr || dateStr.startsWith('0001-01-01')) return '-';
    const date = moment(dateStr);
    return date.isValid() ? date.format('DD MMM YYYY') : '-';
  };

  const maskLicenseKey = (key: string) => {
    if (!key || key.length <= 6) return key ?? 'N/A';
    const first = key.slice(0, 4);
    const last = key.slice(-4);
    const middle = key.slice(4, -4);
    return `${first}${'•'.repeat(middle.length)}${last}`;
  };

  const handleCopy = (fullKey: string) => {
    navigator.clipboard.writeText(fullKey).then(() => {
      setCopied(true);
      addToast('License key copied to clipboard', 'success', 2000);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderDownloadButton = () => {
    switch (downloadState) {
      case 'downloading':
        return (
          <button
            disabled
            className="relative overflow-hidden bg-red-600 text-white text-sm px-6 py-2 rounded-full cursor-wait flex items-center gap-2 min-w-[140px] justify-center"
          >
            <div
              className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-500 to-red-700 animate-shimmer"
              style={{
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
              }}
            />
            <div className="relative flex items-center gap-2">
              <FaSpinner className="animate-spin" size={14} />
              <span className="font-medium">Downloading...</span>
            </div>
          </button>
        );
      case 'success':
        return (
          <button
            disabled
            className="bg-green-600 text-white text-sm px-6 py-2 rounded-full flex items-center gap-2 min-w-[140px] justify-center"
          >
            <FiCheck size={16} className="animate-bounce" />
            <span className="font-medium">Downloaded!</span>
          </button>
        );
      default:
        return (
          <button
            onClick={handleFetchLatestAgent}
            className="bg-red-600 hover:bg-red-700 active:scale-95 text-white text-sm px-6 py-2 rounded-full flex items-center gap-2 min-w-[140px] justify-center transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <FiDownload size={16} />
            <span className="font-medium">Download</span>
          </button>
        );
    }
  };

  return (
    <div className="bg-[#1e1e1e] text-white rounded-xl p-6 w-full mx-auto">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {licenseData ? (
        <>
          <div className="grid grid-cols-3 items-center border-b border-gray-700 py-4 text-base">
            <span className="text-gray-300 whitespace-nowrap">Enterprise Edition</span>
            <span className="text-center text-gray-500">:</span>
            <span className="text-right font-medium leading-relaxed tracking-wide">Enterprise Patch Management License</span>
          </div>

          <div className="grid grid-cols-3 items-center border-b border-gray-700 py-4 text-base">
            <span className="text-gray-300 whitespace-nowrap">License Expiry</span>
            <span className="text-center text-gray-500">:</span>
            <span className="text-right font-medium leading-relaxed tracking-wide">{formatDate(licenseData.activateToDate)}</span>
          </div>

          <div className="grid grid-cols-3 items-center border-b border-gray-700 py-3 text-base">
            <span className="text-gray-300 whitespace-nowrap">License Key</span>
            <span className="text-center text-gray-500">:</span>
            <div className="flex justify-end items-center gap-3">
              <span className="font-mono text-base tracking-wider bg-gray-800/50 px-3 py-1.5 rounded border border-gray-700">
                {maskLicenseKey(licenseData.licenseKey)}
              </span>
              <button
                onClick={() => handleCopy(licenseData.licenseKey)}
                className="bg-gray-700 hover:bg-gray-600 active:scale-95 text-white p-2 rounded transition-all duration-200 group"
                title="Copy license key"
              >
                {copied ? (
                  <FiCheck size={16} className="text-green-400" />
                ) : (
                  <FiCopy size={16} className="group-hover:text-blue-400 transition-colors" />
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 items-center border-b border-gray-700 py-4 text-base">
            <span className="text-gray-300 whitespace-nowrap">Number of Licenses purchased</span>
            <span className="text-center text-gray-500">:</span>
            <span className="text-right font-medium leading-relaxed tracking-wide text-lg">{licenseData.totalLicenses}</span>
          </div>

          <div className="grid grid-cols-3 items-center py-3">
            <span className="text-gray-300">Download Windows Agent</span>
            <span className="text-center text-gray-500">:</span>
            <div className="flex justify-end">{renderDownloadButton()}</div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center py-6 text-gray-400">
          <FaSpinner className="animate-spin mr-2" size={20} />
          <span>Loading license info...</span>
        </div>
      )}
    </div>
  );
};

export default GuidePage;
